import { useEffect, useRef } from 'react';
import { activeWordAt } from '@captionflow/caption-engine';
import { useEditor } from '../../state/editorStore';
import { api } from '../../api/client';
import { playbackClock } from '../../playback/clock';

/**
 * Applies the studio's audio settings during preview:
 *   - original track volume/mute → the stage <video>.
 *   - a background-music track → a hidden <audio>, kept in sync with the clock,
 *     ducked while someone is speaking.
 * No UI of its own. (Export mixing is a follow-up, like transitions.)
 */
export function AudioEngine() {
  const audio = useEditor((s) => s.audio);
  const words = useEditor((s) => s.words);
  const masterVolume = useEditor((s) => s.masterVolume);
  const masterMuted = useEditor((s) => s.masterMuted);
  // "Studio sound": when enabled + ready, play the enhanced track instead of the
  // video's own audio (and mute the video), so the preview A/Bs the export.
  const enhOn = useEditor((s) => s.audioEnhance.enabled && s.audioEnhance.status === 'ready' && !!s.audioEnhance.url);
  const enhUrl = useEditor((s) => s.audioEnhance.url);
  const musicRef = useRef<HTMLAudioElement>(null);
  const enhRef = useRef<HTMLAudioElement>(null);
  const model = useRef({ audio, words, masterVolume, masterMuted, enhOn });
  model.current = { audio, words, masterVolume, masterMuted, enhOn };

  const master = () => (masterMuted ? 0 : masterVolume);

  // Hydrate the saved enhance state once so the A/B preview works even if the
  // Audio tab is never opened (the export reads the server copy regardless).
  useEffect(() => {
    const projectId = useEditor.getState().projectId;
    if (!projectId) return;
    api
      .getAudioEnhance(projectId)
      .then((r) =>
        useEditor.getState().setAudioEnhance({ status: r.status, progress: r.progress, enabled: r.enabled, strength: r.strength, loudnorm: r.loudnorm, url: r.url, stale: r.stale, error: r.error }),
      )
      .catch(() => undefined);
  }, []);

  // Original track: MASTER × original level onto the video element. When the
  // enhanced track is driving audio, the video is muted (the <audio> plays it).
  useEffect(() => {
    const v = document.querySelector<HTMLVideoElement>('.studio-video');
    if (v) {
      v.volume = master() * (audio.originalMuted ? 0 : audio.originalVolume);
      v.muted = masterMuted || audio.originalMuted || enhOn;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audio.originalVolume, audio.originalMuted, masterVolume, masterMuted, enhOn]);

  // Point the enhanced-audio element at the cleaned track (or clear it).
  useEffect(() => {
    const a = enhRef.current;
    if (!a) return;
    if (enhOn && enhUrl) {
      if (a.src !== enhUrl) a.src = enhUrl;
    } else {
      a.pause();
      a.removeAttribute('src');
      a.load();
    }
  }, [enhOn, enhUrl]);

  // Keep the enhanced track locked 1:1 to the transport (no window/loop/duck).
  useEffect(() => {
    const a = enhRef.current;
    if (!a) return;
    const apply = (t: number, playing: boolean) => {
      if (!model.current.enhOn) {
        if (!a.paused) a.pause();
        return;
      }
      const mst = model.current.masterMuted ? 0 : model.current.masterVolume;
      const lvl = model.current.audio.originalMuted ? 0 : model.current.audio.originalVolume;
      a.volume = Math.max(0, Math.min(1, mst * lvl));
      const target = t / 1000;
      if (playing) {
        if (Math.abs(a.currentTime - target) > 0.3) a.currentTime = Math.max(0, target);
        if (a.paused) void a.play().catch(() => undefined);
      } else if (!a.paused) {
        a.pause();
        a.currentTime = Math.max(0, target);
      }
    };
    const unsub = playbackClock.subscribe(apply);
    return unsub;
  }, []);

  // Background music: point the element at the track.
  useEffect(() => {
    const a = musicRef.current;
    if (!a) return;
    if (audio.music) {
      if (a.src !== audio.music.url) a.src = audio.music.url;
      a.loop = audio.music.loop;
    } else {
      a.pause();
      a.removeAttribute('src');
      a.load();
    }
  }, [audio.music?.url, audio.music?.loop]);

  // Keep the music in sync with the transport + duck under speech.
  useEffect(() => {
    const a = musicRef.current;
    if (!a) return;
    const apply = (t: number, playing: boolean) => {
      const m = model.current.audio.music;
      if (!m) {
        if (!a.paused) a.pause();
        return;
      }
      // Only play within the music's time window on the timeline.
      const start = m.startMs ?? 0;
      const end = start + (m.durationMs ?? Number.POSITIVE_INFINITY);
      if (t < start || t > end) {
        if (!a.paused) a.pause();
        return;
      }
      const speaking = m.duck && !!activeWordAt(model.current.words, t);
      const mst = model.current.masterMuted ? 0 : model.current.masterVolume;
      a.volume = Math.max(0, Math.min(1, mst * m.volume * (speaking ? 0.32 : 1)));
      // Position within the track (relative to the window start), looping over
      // the file length when the window is longer than the track.
      const rel = (t - start) / 1000;
      const td = a.duration && Number.isFinite(a.duration) ? a.duration : 0;
      const target = m.loop && td > 0 ? rel % td : rel;
      if (playing) {
        if (Math.abs(a.currentTime - target) > 0.3) a.currentTime = Math.max(0, target);
        if (a.paused) void a.play().catch(() => undefined);
      } else if (!a.paused) {
        a.pause();
        a.currentTime = Math.max(0, target);
      }
    };
    const unsub = playbackClock.subscribe(apply);
    return unsub;
  }, []);

  return (
    <>
      <audio ref={musicRef} preload="auto" hidden />
      <audio ref={enhRef} preload="auto" hidden />
    </>
  );
}
